<template>
  <div class="tree-view">
    <ul class="tree-list">
      <li
        v-for="(item, index) in items"
        :key="getId(item) || index"
        class="tree-item"
      >
        <!-- Kartu node -->
        <div
          class="tree-node"
          :class="{
            'tree-node-root': level === 0,
            'tree-node-selected': selectedId === getId(item),
          }"
          @click="selectNode(item)"
        >
          <!-- Ikon -->
          <div class="tree-node-icon">
            <v-icon color="white" size="22">
              {{ getIcon(item) }}
            </v-icon>
          </div>

          <!-- Informasi node -->
          <div class="tree-node-content">
            <div v-if="getSubtitle(item)" class="tree-node-subtitle">
              {{ getSubtitle(item) }}
            </div>

            <div class="tree-node-title">
              {{ getLabel(item) }}
            </div>

            <div v-if="getDescription(item)" class="tree-node-description">
              {{ getDescription(item) }}
            </div>

            <v-divider class="my-2"></v-divider>

            <!-- SLOT CUSTOM -->
            <div
              v-if="$scopedSlots.custom || $slots.custom"
              class="tree-node-custom"
              @click.stop
            >
              <slot
                name="custom"
                :item="item"
                :level="level"
                :expanded="isExpanded(item)"
                :has-children="getChildren(item).length > 0"
                :toggle="() => toggleNode(item)"
                :select="() => selectNode(item)"
              />
            </div>
          </div>

          <!-- Tombol expand/collapse bawaan -->
          <v-btn
            v-if="getChildren(item).length"
            icon
            x-small
            class="tree-toggle"
            :aria-label="isExpanded(item) ? 'Tutup cabang' : 'Buka cabang'"
            @click.stop="toggleNode(item)"
          >
            <v-icon small>
              {{ isExpanded(item) ? "mdi-chevron-up" : "mdi-chevron-down" }}
            </v-icon>
          </v-btn>
        </div>

        <!-- Anak node: komponen memanggil dirinya sendiri -->
        <div
          v-if="getChildren(item).length && isExpanded(item)"
          class="tree-branch"
        >
          <tree-view
            :items="getChildren(item)"
            :options="options"
            :level="level + 1"
            :selected-id="selectedId"
            :expand-all="expandAll"
            :expand-version="expandVersion"
            @select="$emit('select', $event)"
          >
            <!-- Teruskan slot custom ke seluruh level anak -->
            <template v-slot:custom="slotProps">
              <slot name="custom" v-bind="slotProps" />
            </template>
          </tree-view>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "TreeView",

  props: {
    items: {
      type: Array,
      default: () => [],
    },

    options: {
      type: Object,
      default: () => ({}),
    },

    level: {
      type: Number,
      default: 0,
    },

    selectedId: {
      default: null,
    },

    expandAll: {
      type: Boolean,
      default: false,
    },

    expandVersion: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      expandedNodes: {},
    };
  },

  computed: {
    config() {
      return Object.assign(
        {
          id: "id",
          label: "nama",
          children: "bawahan",
          subtitle: "",
          description: "",
          icon: "",
          defaultExpandedDepth: 1,
        },
        this.options || {},
      );
    },
  },

  watch: {
    expandVersion() {
      this.expandedNodes = {};
    },
  },

  methods: {
    getValue(item, field) {
      if (!field) return "";

      return field.split(".").reduce((value, key) => {
        return value == null ? undefined : value[key];
      }, item);
    },

    getId(item) {
      return this.getValue(item, this.config.id);
    },

    getLabel(item) {
      return this.getValue(item, this.config.label) || "Tanpa nama";
    },

    getSubtitle(item) {
      return this.getValue(item, this.config.subtitle);
    },

    getDescription(item) {
      return this.getValue(item, this.config.description);
    },

    getIcon(item) {
      return this.getValue(item, this.config.icon) || "mdi-file-tree-outline";
    },

    getChildren(item) {
      const children = this.getValue(item, this.config.children);

      return Array.isArray(children) ? children : [];
    },

    isExpanded(item) {
      const id = this.getId(item);

      if (this.expandAll) return true;

      if (Object.prototype.hasOwnProperty.call(this.expandedNodes, id)) {
        return this.expandedNodes[id];
      }

      return this.level < this.config.defaultExpandedDepth;
    },

    toggleNode(item) {
      const id = this.getId(item);

      this.$set(this.expandedNodes, id, !this.isExpanded(item));
    },

    selectNode(item) {
      this.$emit("select", item);
    },
  },
};
</script>

<style scoped>
/* =========================================
   TREE CONTAINER
========================================= */
.tree-view {
  position: relative;
  width: 100%;
}

.tree-list {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-width: max-content;
  padding: 0;
  margin: 0;
}

.tree-item {
  position: relative;
  flex: 0 0 auto;
  list-style: none;
  text-align: center;
  padding: 32px 10px 0;
  box-sizing: border-box;
}

/* =========================================
   NODE CARD
========================================= */
.tree-node {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  width: 280px;
  min-height: 120px;
  margin: 0 auto;
  padding: 18px;
  text-align: left;
  background: #ffffff;
  border: 1px solid #dbe4ef;
  border-radius: 16px;
  box-shadow: 0 5px 16px rgba(15, 23, 42, 0.06);
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.tree-node:hover {
  border-color: #93c5fd;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.1);
  transform: translateY(-2px);
}

.tree-node-selected {
  border: 2px solid #1976d2;
}

/* =========================================
   ROOT CARD
========================================= */
.tree-node-root {
  width: 350px;
  min-height: 120px;
  color: #ffffff;
  background: #166534;
  border-color: #166534;
}

.tree-node-root:hover {
  background: #166534;
  border-color: #15803d;
}

/* =========================================
   NODE ICON
========================================= */
.tree-node-icon {
  display: flex;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-right: 14px;
  border-radius: 12px;
  background: #1976d2;
}

.tree-node-root .tree-node-icon {
  background: rgba(255, 255, 255, 0.22);
}

/* =========================================
   NODE CONTENT
========================================= */
.tree-node-content {
  flex: 1;
  min-width: 0;
}

.tree-node-subtitle {
  margin-bottom: 6px;
  color: #64748b;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.tree-node-root .tree-node-subtitle {
  color: #bbf7d0;
}

.tree-node-title {
  color: #1e293b;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.7;
  overflow-wrap: anywhere;
}

.tree-node-root .tree-node-title {
  color: #ffffff;
  font-size: 14px;
}

.tree-node-description {
  margin-top: 8px;
  color: #64748b;
  font-size: 11px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.tree-node-root .tree-node-description {
  color: #dcfce7;
}

/* =========================================
   CUSTOM SLOT
========================================= */
.tree-node-custom {
  position: relative;
  z-index: 3;
}

.tree-node-custom .v-btn {
  letter-spacing: normal;
  text-transform: none;
}

/* =========================================
   EXPAND / COLLAPSE
========================================= */
.tree-toggle {
  flex: 0 0 auto;
  margin-left: 4px;
  color: #64748b;
}

.tree-node-root .tree-toggle {
  color: #ffffff;
}

/* =========================================
   HIERARCHY CONNECTORS
========================================= */
.tree-branch {
  position: relative;
  width: 100%;
  padding-top: 24px;
  box-sizing: border-box;
}

.tree-branch::before {
  content: "";
  position: absolute;
  top: -2px;
  left: 50%;
  height: 26px;
  border-left: 2px solid #cbd5e1;
  transform: translateX(-1px);
  z-index: 1;
  pointer-events: none;
}

.tree-branch > .tree-view > .tree-list > .tree-item::after {
  content: "";
  position: absolute;
  top: 24px;
  left: 0;
  right: 0;
  border-top: 2px solid #cbd5e1;
  z-index: 0;
  pointer-events: none;
}

.tree-branch > .tree-view > .tree-list > .tree-item::before {
  content: "";
  position: absolute;
  top: 24px;
  left: 50%;
  height: 32px;
  border-left: 2px solid #cbd5e1;
  transform: translateX(-1px);
  z-index: 1;
  pointer-events: none;
}

.tree-branch > .tree-view > .tree-list > .tree-item:first-child::after {
  left: 50%;
}

.tree-branch > .tree-view > .tree-list > .tree-item:last-child::after {
  right: 50%;
}

.tree-branch > .tree-view > .tree-list > .tree-item:only-child::after {
  display: none;
}

/* =========================================
   RESPONSIVE
========================================= */
@media (max-width: 600px) {
  .tree-list {
    justify-content: flex-start;
  }

  .tree-item {
    padding-left: 7px;
    padding-right: 7px;
  }

  .tree-node {
    width: 250px;
    min-height: 100px;
    padding: 14px;
    border-radius: 13px;
  }

  .tree-node-root {
    width: 290px;
    min-height: 110px;
  }

  .tree-node-icon {
    flex-basis: 36px;
    width: 36px;
    height: 36px;
    margin-right: 10px;
  }

  .tree-node-title {
    font-size: 11px;
  }

  .tree-node-root .tree-node-title {
    font-size: 13px;
  }

  .tree-node-description {
    font-size: 10px;
  }

  .tree-node-custom {
    margin-left: 4px;
    gap: 2px;
  }
}
</style>
